/**
 * IndicatorAttribution.jsx
 * Component for displaying data source attributions for specific indicators
 * Currently used for R3 (Sea level) - BRGM attribution (overseas territories only)
 */
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Link, Stack, Typography } from '@mui/material';
import { printTextSize } from '../utils/entityHelpers';
import { selectCurrentEntity } from '../store/slices/navigationSlice';

/**
 * Attribution configuration for indicators requiring source credits
 * @type {Object}
 */
const ATTRIBUTIONS = {
  R3: {
    logo: '/logo-brgm.svg',
    logoAlt: 'Logo BRGM',
    logoHeight: 40,
    text: 'Source : BRGM DOI 10.5802',
    link: 'https://doi.org/10.5802/crgeos.290',
    linkText: 'Accéder à l\'article de référence',
    // Now displayed for all entities, not just overseas territories
    // // Only show for overseas territories
    // shouldDisplay: (entity) => OVERSEAS_TERRITORIES.isOverseas(entity?.domaine),
  },
  AG9: {
    logo: '/logo-clipssa.svg',
    logoAlt: 'Logo CLIPSSA',
    logoHeight: 40,
    text: 'Source : projet CLIPSSA (IRD, AFD, Météo-France)',
    link: 'https://clipssa.org/',
    // No shouldDisplay needed: AG9 is only defined for Nouvelle-Calédonie
    // (see data/texts/indicators/index.json), so attribution shows there only.
  },
};

/**
 * Component to display attribution/credit for data sources
 * Uses Redux to access current entity and determine if attribution should be shown
 * @param {Object} props - Component props
 * @param {string} props.indicatorKey - Indicator identifier (e.g., 'R3')
 * @param {Object} [props.sx] - Additional MUI sx styles
 * @returns {React.ReactElement|null} Attribution component or null if no attribution needed
 */
const IndicatorAttribution = ({ indicatorKey, sx = {} }) => {
  const entity = useSelector(selectCurrentEntity);
  const attribution = ATTRIBUTIONS[indicatorKey];

  // No attribution configured for this indicator
  if (!attribution) {
    return null;
  }

  // Check if attribution should be displayed for this entity
  if (attribution.shouldDisplay && !attribution.shouldDisplay(entity)) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      sx={{
        mt: 2,
        mb: 2,
        ...sx,
      }}
    >
      {/* Attribution text and link */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{ ...printTextSize }}
        >
          {attribution.link && (
            <Link
              href={attribution.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {attribution.text}
            </Link>
          )}
          {!attribution.link && (attribution.text)}
        </Typography>
      </Box>

      {/* Logo */}
      {attribution.logo && (
        <Box
          component="img"
          src={attribution.logo}
          alt={attribution.logoAlt}
          sx={{
            height: attribution.logoHeight,
            width: 'auto',
          }}
        />
      )}
    </Stack>
  );
};

IndicatorAttribution.displayName = 'IndicatorAttribution';

export default memo(IndicatorAttribution);
